import fromExponential from 'from-exponential';
import { AssetFormat, PortfolioAssetType } from 'services/requests';
import { formatUSD } from 'utils';
import { CustomColumn } from 'components/Table/Table.types';
import Currency from 'components/Currency';
import PriceChange from 'components/PriceChange';

export type MapData = PortfolioAssetsTableSign[];

export type PortfolioAssetsTableSign = Omit<
  PortfolioAssetType,
  'amount' | 'rate' | 'equivalent' | 'todayDifferencePercent' | 'estCostBasis'
> & {
  amount: string;
  rate: string;
  equivalent: string;
  todayDifferencePercent: number | null;
  estCostBasis: string;
};

export const mapData = (data: PortfolioAssetType[] | undefined): MapData | undefined =>
  data?.map((assetItem: PortfolioAssetType) => {
    const amount =
      assetItem.asset.assetFormat !== AssetFormat.FIAT
        ? `${fromExponential(assetItem.amount)} ${assetItem.asset.code}`
        : '\u2013';

    const rate =
      assetItem.asset.assetFormat !== AssetFormat.FIAT
        ? formatUSD(assetItem.rate)
        : '\u2013';

    const todayDifferencePercent =
      assetItem.asset.assetFormat !== AssetFormat.FIAT
        ? assetItem.todayDifferencePercent
        : null;

    const estCostBasis =
      assetItem.estCostBasis && assetItem.asset.assetFormat !== AssetFormat.FIAT
        ? formatUSD(assetItem.estCostBasis)
        : '\u2013';

    return {
      ...assetItem,
      amount,
      rate,
      todayDifferencePercent,
      equivalent: formatUSD(assetItem.equivalent),
      estCostBasis,
    };
  });

const Columns = {
  CURRENCY: 'currency',
  USD_AMOUNT: 'usdAmount',
  CRYPTO_AMOUNT: 'cryptoAmount',
  PRICE_CHANGE: 'priceChange',
  USD_PRICE: 'usdPrice',
  EST_COST_BASIS: 'estCostBasis',
} as const;

export type AssetsColumnsType = typeof Columns[keyof typeof Columns];

const columns: {
  [id in AssetsColumnsType]: CustomColumn<PortfolioAssetsTableSign>;
} = {
  [Columns.CURRENCY]: {
    id: Columns.CURRENCY,
    Header: 'Currency',
    accessor: 'asset',
    Cell: ({ cell: { value }, row }) => (
      <Currency asset={value} walletKind={row.original.walletKind} />
    ),
    cellTitle: (item?: PortfolioAssetsTableSign['asset']) => item?.name,
  },
  [Columns.USD_AMOUNT]: {
    id: Columns.USD_AMOUNT,
    Header: 'USD amount',
    accessor: 'equivalent',
  },
  [Columns.CRYPTO_AMOUNT]: {
    id: Columns.CRYPTO_AMOUNT,
    Header: 'Crypto amount',
    accessor: 'amount',
  },
  [Columns.EST_COST_BASIS]: {
    id: Columns.EST_COST_BASIS,
    Header: 'Est. cost basis',
    accessor: 'estCostBasis',
  },
  [Columns.PRICE_CHANGE]: {
    id: Columns.PRICE_CHANGE,
    Header: 'Price change (24h)',
    accessor: 'todayDifferencePercent',
    Cell: ({ cell: { value } }) =>
      value ? <PriceChange amount={value} /> : <>{'\u2013'}</>,
  },
  [Columns.USD_PRICE]: {
    id: Columns.USD_PRICE,
    Header: 'USD price',
    accessor: 'rate',
  },
};

export const getColumns = (cols: AssetsColumnsType[]) =>
  cols.map((colId) => columns[colId]);
